import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "desktop-asset-cache";
const CACHE_VERSION = 1;

export interface CachedAsset {
  id: string;
  name: string;
  parent_id: string | null;
  list_id: string;
  subAssetCount: number;
}

export interface CachedAssetList {
  id: string;
  title: string;
}

interface AssetCache {
  version: number;
  updatedAt: string;
  assetLists: CachedAssetList[];
  assets: CachedAsset[];
}

function readCache(): AssetCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AssetCache;
    if (parsed.version !== CACHE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(cache: AssetCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage full or unavailable – silently ignore
  }
}

/**
 * Read cached asset data. Returns null if no cache exists.
 */
export function getCachedAssets(): { assetLists: CachedAssetList[]; assets: CachedAsset[] } | null {
  const cache = readCache();
  if (!cache) return null;
  return { assetLists: cache.assetLists, assets: cache.assets };
}

/**
 * Fetch all asset lists and assets from the database and write them to localStorage.
 * Call this after any CRUD operation in the main app.
 */
export async function refreshAssetCache(): Promise<void> {
  try {
    // Fetch asset lists
    const { data: lists, error: listsError } = await supabase
      .from("asset_lists")
      .select("id, title")
      .order("title", { ascending: true });

    if (listsError) {
      console.error("Error refreshing asset cache (lists):", listsError);
      return;
    }

    const listIds = (lists || []).map((l) => l.id);
    if (listIds.length === 0) {
      writeCache({
        version: CACHE_VERSION,
        updatedAt: new Date().toISOString(),
        assetLists: [],
        assets: [],
      });
      return;
    }

    // Fetch all assets across accessible lists
    const { data: assetsData, error: assetsError } = await supabase
      .from("assets")
      .select("id, name, parent_id, list_id")
      .in("list_id", listIds)
      .order("name", { ascending: true });

    if (assetsError) {
      console.error("Error refreshing asset cache (assets):", assetsError);
      return;
    }

    const allAssets = assetsData || [];

    // Compute sub-asset counts in-memory
    const childCountMap = new Map<string, number>();
    for (const asset of allAssets) {
      if (asset.parent_id) {
        childCountMap.set(asset.parent_id, (childCountMap.get(asset.parent_id) || 0) + 1);
      }
    }

    const cachedAssets: CachedAsset[] = allAssets.map((a) => ({
      id: a.id,
      name: a.name,
      parent_id: a.parent_id,
      list_id: a.list_id,
      subAssetCount: childCountMap.get(a.id) || 0,
    }));

    writeCache({
      version: CACHE_VERSION,
      updatedAt: new Date().toISOString(),
      assetLists: lists || [],
      assets: cachedAssets,
    });
  } catch (error) {
    console.error("Error refreshing asset cache:", error);
  }
}
